import { useCallback, useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import styled from 'styled-components';
import type {
  Transcription,
  TranscriptionSource,
} from '@frameio/graphql-client/generated';
import { useGetAssetThumbnailsQuery } from '@frameio/graphql-client/generated';
import { select } from '@frameio/i18n';
import {
  AlertInline,
  Box,
  Button2,
  LinearProgress,
  noop,
  PlaceholderTranscription,
  Spinner,
  styledScrollbarMixin,
  Text,
  theme,
  Toggle,
  ToggleCheckbox,
  ToggleLabel,
  Tooltip,
} from '@frameio/vapor';
import { IconInfo } from '@frameio/vapor-icons';
import { useActiveAccountId } from '../../contexts';
import {
  useBackendUserPrefs,
  useGeoFeature,
  useTranscriptionManager,
} from '../../hooks';
import type { MediaDataTypename } from '../../utils/transcodes';
import { LanguageMenu } from '../LanguageMenu';
import { TranscriptionConsentModal } from './components/TranscriptionConsentModal';
import TEXT from './generated/TEXT/GenerateTranscriptionForm';

const Break = () => <br />;

TEXT.DEFINE(
  {
    /** Body text for add-language view. */
    addLanguageBody:
      'Select a different language to generate a new Transcript & Captions for your asset.',
    /** Title for add-language view. */
    addLanguageTitle: 'Add Transcript Language',
    /** Note explaining language selection limitations. */
    addLanguageNote:
      'Transcripts will not be translated. Accuracy will be dependent on selected language matching spoken language.',
    /** Placeholder for the language dropdown. */
    addLanguagePlaceholder: 'Choose language',
    /** Body text for the default view. */
    body: 'Transcribe and caption your asset to streamline navigation and feedback.',
    /** Primary action button label. */
    buttonLabel: 'Transcribe',
    /** Cancel button label. */
    cancel: 'Cancel',
    /** Clear search button label. */
    clearSearch: 'Clear language search',
    /** Tooltip copy when transcription is disabled. */
    disabledByAdministrator: 'Disabled by your Administrator',
    /** Notice when speaker labeling is unavailable. */
    speakerLabelingUnavailable: 'Speaker labeling not available',
    /** Tooltip for disabled speaker diarization. */
    speakerDiarizationDisabledTooltip: 'Unavailable in your location',
    /** Error body shown when transcription fails. */
    errorBody:
      'An error occurred while transcribing and generating captions for this asset. Please try again later.',
    /** Error title shown when transcription fails. */
    errorTitle: 'Something went wrong',
    /** Feedback action label. */
    feedback: 'Submit feedback',
    /** Body text while uploading captions. */
    captionsUploadBody: 'You can close this panel during upload.',
    /** Body text while generating transcript and captions. */
    generationCompletionTimeBody: () => (
      <>
        Completion time will depend on asset length.
        <Break />
        You can close this panel during generation.
      </>
    ),
    /** Title text while generating or uploading. */
    generatingTitle: (source?: TranscriptionSource | undefined) => {
      const transcriptionSource = source ?? '';
      return select(transcriptionSource, {
        UPLOAD: 'Uploading Captions…',
        other: 'Generating Transcript & Captions…',
      });
    },
    /** Label for loading spinner. */
    loadingLabel: 'Loading',
    /** Label for the speaker labeling toggle. */
    labelSpeakers: 'Label Speakers',
    /** Disclaimer text about transcript accuracy and auto detection. */
    transcriptsDisclaimer: () => (
      <>
        Accuracy may vary depending on the asset.
        <Break />
        Selected language must match spoken language. If you select{' '}
        <strong>Auto</strong>, the language detected may vary from what's
        spoken.
      </>
    ),
    /** Disclaimer text for captions output. */
    captionsDisclaimer: 'Selected language will appear in Captions menu.',
    /** Retry button label. */
    retry: 'Retry',
    /** Dialog title. */
    title: 'Frame.io Transcription',
    /** Warning shown when assets have no audio. */
    noAudioWarning:
      "Some selected assets do not have audio and won't be transcribed.",
  },
  { Break }
);
const BUTTON_WIDTH = 91;
const TITLE_PADDING_TOP = 12;
type GenerateTranscriptionFormProps = {
  assetIds?: string[];
  isAddLanguageMode?: boolean;
  onError?: () => void;
  onSuccess?: (id: string) => void;
  onFeedback?: () => void;
  mediaType?: MediaDataTypename;
  showIllustration?: boolean;
  showFooter?: boolean;
  showFeedbackButton?: boolean;
  transcriptions?: Transcription[];
  activeTranscription: Transcription | null;
  isTranscriptionFeatureDisabled?: boolean;
};
// Create motion components
const MotionText = motion(Text);
// Animation variants
const fadeInOut = {
  initial: { opacity: 0 },
  animate: { opacity: 1, transition: { duration: 0.3 } },
  exit: { opacity: 0, transition: { duration: 0.2 } },
};
const slideUpFade = {
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.4 } },
  exit: { opacity: 0, y: -24, transition: { duration: 0.3 } },
};
const fadeIn = {
  initial: { opacity: 0 },
  animate: { opacity: 1, transition: { duration: 0.2 } },
  exit: { opacity: 0, transition: { duration: 0.2 } },
};
export const GenerateTranscriptionForm = ({
  assetIds,
  isAddLanguageMode = false,
  onError = noop,
  onSuccess = noop,
  onFeedback = noop,
  showIllustration = false,
  showFooter = false,
  showFeedbackButton = false,
  transcriptions = [],
  activeTranscription,
  isTranscriptionFeatureDisabled = false,
}: GenerateTranscriptionFormProps) => {
  const accountId = useActiveAccountId();
  const { data } = useGetAssetThumbnailsQuery({
    skip: !accountId || !assetIds || assetIds?.length === 0,
    variables: {
      accountId: accountId ?? '',
      assetIds: assetIds || [],
      first: assetIds?.length ?? 0,
    },
  });
  const { isPermitted: canToggleSpeakerDiarization, loading: geoCheckLoading } =
    useGeoFeature({
      feature: 'canToggleSpeakerDiarization',
    });
  const {
    setUserPrefs: setBackendUserPrefs,
    userPrefs: { hasProvidedTranscriptionDiarizationConsent },
  } = useBackendUserPrefs();
  const {
    handleRetry,
    transcribe,
    transcribeError,
    transcribeLoading,
    handleCancel,
    isDiarizationChecked,
    setIsDiarizationChecked,
    setTranscriptionsLanguage,
    transcriptionsLanguage,
  } = useTranscriptionManager({
    assetIds,
    onSuccess,
    onError,
  });
  const [selectedLanguage, setSelectedLanguage] = useState<string | null>(null);
  const selectLocale = useCallback(
    (value: string) => {
      setTranscriptionsLanguage(value);
      if (isAddLanguageMode) {
        setSelectedLanguage(value);
      }
    },
    [setTranscriptionsLanguage, isAddLanguageMode]
  );
  const isLoading =
    transcribeLoading ||
    (activeTranscription?.encodeStatus === 'STARTED' && !isAddLanguageMode) ||
    (transcriptions?.some((t) => t.encodeStatus === 'STARTED') &&
      !activeTranscription &&
      !isAddLanguageMode);
  const error = transcribeError;
  const showNoAudioMessage = useMemo(() => {
    if (!assetIds || assetIds?.length < 0) return false;
    return data?.account?.assets?.nodes.some(
      (asset) =>
        asset.__typename === 'VideoAsset' && !asset.media?.metadata?.hasAudio
    );
  }, [assetIds, data]);
  const [isConsentModalOpen, setIsConsentModalOpen] = useState(false);
  const showConsentModal = useCallback(() => {
    setIsConsentModalOpen(true);
  }, []);
  const handleConsentModalSubmit = useCallback(
    (rememberConsentChoice: boolean) => {
      setIsConsentModalOpen(false);
      if (rememberConsentChoice) {
        setBackendUserPrefs({
          hasProvidedTranscriptionDiarizationConsent: true,
        });
      }
      transcribe();
    },
    [transcribe, setBackendUserPrefs]
  );
  const handleConsentModalClose = useCallback(() => {
    setIsConsentModalOpen(false);
    setIsDiarizationChecked(false);
  }, [setIsDiarizationChecked]);
  const handleToggleSpeakerDiarization = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setIsDiarizationChecked(e.target.checked);
      if (!e.target.checked && hasProvidedTranscriptionDiarizationConsent) {
        // Clear consent
        setBackendUserPrefs({
          hasProvidedTranscriptionDiarizationConsent: false,
        });
      }
    },
    [
      hasProvidedTranscriptionDiarizationConsent,
      setIsDiarizationChecked,
      setBackendUserPrefs,
    ]
  );
  const action = useCallback(() => {
    if (error) return handleRetry();
    if (isDiarizationChecked && !hasProvidedTranscriptionDiarizationConsent) {
      return showConsentModal();
    }
    return transcribe();
  }, [
    error,
    hasProvidedTranscriptionDiarizationConsent,
    showConsentModal,
    transcribe,
    handleRetry,
    isDiarizationChecked,
  ]);
  // Extract TEXT properties to avoid calling hooks inside useMemo
  const textErrorTitle = TEXT.errorTitle;
  const textAddLanguageTitle = TEXT.addLanguageTitle;
  const textGeneratingTitle = TEXT.generatingTitle;
  const textTitle = TEXT.title;
  const textErrorBody = TEXT.errorBody;
  const textAddLanguageBody = TEXT.addLanguageBody;
  const textCaptionsUploadBody = TEXT.captionsUploadBody;
  const textGenerationCompletionTimeBody = TEXT.generationCompletionTimeBody;
  const textBody = TEXT.body;
  const title = useMemo(() => {
    if (error) return textErrorTitle;
    if (isAddLanguageMode) return textAddLanguageTitle;
    if (isLoading)
      return textGeneratingTitle(activeTranscription?.source ?? undefined);
    return textTitle;
  }, [
    activeTranscription?.source,
    error,
    isAddLanguageMode,
    isLoading,
    textErrorTitle,
    textAddLanguageTitle,
    textGeneratingTitle,
    textTitle,
  ]);
  const [body, bodyKey] = useMemo(() => {
    if (error) return [textErrorBody, 'error-body'];
    if (isAddLanguageMode) return [textAddLanguageBody, 'add-language-body'];
    if (isLoading) {
      if (activeTranscription?.source === 'UPLOAD') {
        return [textCaptionsUploadBody, 'generating-upload-body'];
      }
      return [textGenerationCompletionTimeBody(), 'generating-body'];
    }
    return [textBody, 'body'];
  }, [
    error,
    isAddLanguageMode,
    isLoading,
    activeTranscription?.source,
    textErrorBody,
    textAddLanguageBody,
    textCaptionsUploadBody,
    textGenerationCompletionTimeBody,
    textBody,
  ]);
  return (
    <StyledContent data-testid="generate-transcription-form">
      <TopContent>
        {showIllustration && (
          <StyledTranscription>
            <PlaceholderTranscription />
          </StyledTranscription>
        )}
        <Box
          alignItems="center"
          display="flex"
          gap={12}
          paddingBottom={12}
          style={{
            paddingTop: showIllustration ? `${TITLE_PADDING_TOP}px` : undefined,
          }}
        >
          <AnimatePresence initial={false} mode="popLayout">
            <MotionText
              key={String(title)}
              animate="animate"
              aria-level={2}
              color="primary"
              data-testid="generate-transcript-form-title"
              exit="exit"
              fontWeight="semiBold"
              initial="initial"
              role="heading"
              variant="header"
              variants={fadeInOut}
            >
              {title}
            </MotionText>
          </AnimatePresence>
        </Box>

        <AnimatePresence initial={false} mode="popLayout">
          <MotionText
            key={bodyKey}
            animate="animate"
            as={StyledBodyText}
            data-testid="generate-transcript-form-body"
            exit="exit"
            initial="initial"
            textAlign="center"
            variant="body"
            variants={fadeInOut}
          >
            {body}
          </MotionText>
        </AnimatePresence>

        <AnimatePresence initial={false} mode="popLayout">
          {isLoading && !error ? (
            <motion.div
              key="loader"
              animate="animate"
              exit="exit"
              initial="initial"
              style={{ width: '100%' }}
              variants={slideUpFade}
            >
              <LoaderContainer data-testid="generate-transcript-form-loader">
                <LinearProgress
                  aria-label={`${TEXT.generatingTitle}`}
                  value={100}
                  variant="gradient"
                />
              </LoaderContainer>
            </motion.div>
          ) : (
            <motion.div
              key="controls"
              animate="animate"
              exit="exit"
              initial="initial"
              variants={slideUpFade}
            >
              {showNoAudioMessage && (
                <Box paddingX={36} paddingY={12}>
                  <StyledAlertLine>{TEXT.noAudioWarning}</StyledAlertLine>
                </Box>
              )}
              <TriggerMenuAndButton
                style={{
                  marginTop: !showNoAudioMessage
                    ? theme.spaces[24]
                    : theme.spaces[8],
                }}
              >
                {!error && (
                  <LanguageMenu
                    initialLanguage={transcriptionsLanguage ?? undefined}
                    isAddLanguageMode={isAddLanguageMode}
                    isDisabled={isTranscriptionFeatureDisabled}
                    selectedLanguage={selectedLanguage}
                    selectLocale={selectLocale}
                    transcriptions={transcriptions ?? []}
                  />
                )}

                {error && (
                  <Button2
                    data-testid="generate-transcript-form-cancel-button"
                    minWidth={`${BUTTON_WIDTH}px`}
                    onPress={handleCancel}
                  >
                    {TEXT.cancel}
                  </Button2>
                )}
                <Tooltip
                  shouldRender={isTranscriptionFeatureDisabled}
                  title={TEXT.disabledByAdministrator}
                >
                  <span>
                    <Button2
                      data-testid="generate-transcript-form-action-button"
                      isDisabled={
                        isTranscriptionFeatureDisabled ||
                        (isAddLanguageMode && !selectedLanguage)
                      }
                      isLoading={transcribeLoading}
                      minWidth={`${BUTTON_WIDTH}px`}
                      onPress={action}
                      variant="primary"
                    >
                      {error ? TEXT.retry : TEXT.buttonLabel}
                    </Button2>
                  </span>
                </Tooltip>
              </TriggerMenuAndButton>
              <Box display="flex" justifyContent="center" paddingTop={12}>
                <AnimatePresence initial={false} mode="popLayout">
                  {geoCheckLoading && (
                    <motion.div
                      key="loader"
                      animate="animate"
                      exit="exit"
                      initial="initial"
                      style={{ height: '20px' }}
                      variants={fadeIn}
                    >
                      <Box
                        display="flex"
                        justifyContent="center"
                        paddingTop={2}
                      >
                        <Spinner
                          aria-label={TEXT.loadingLabel}
                          delay={0}
                          isLoading={true}
                          size="xx-small"
                        />
                      </Box>
                    </motion.div>
                  )}
                  {!geoCheckLoading && !canToggleSpeakerDiarization && (
                    <motion.div
                      key="controls"
                      animate="animate"
                      exit="exit"
                      initial="initial"
                      variants={fadeIn}
                    >
                      <Box alignItems="center" display="flex" gap={4}>
                        <Text
                          color="secondary"
                          textAlign="center"
                          variant="body"
                        >
                          {TEXT.speakerLabelingUnavailable}
                        </Text>
                        <Tooltip
                          delayIn={0}
                          placement="bottom"
                          title={TEXT.speakerDiarizationDisabledTooltip}
                        >
                          <IconInfo
                            color={theme.colors.icon.secondary}
                            size={16}
                          />
                        </Tooltip>
                      </Box>
                    </motion.div>
                  )}
                  {!geoCheckLoading && canToggleSpeakerDiarization && (
                    <motion.div
                      key="controls"
                      animate="animate"
                      exit="exit"
                      initial="initial"
                      variants={fadeIn}
                    >
                      <CheckboxWrapper
                        $disabled={isTranscriptionFeatureDisabled}
                      >
                        <Toggle
                          checked={isDiarizationChecked}
                          disabled={isTranscriptionFeatureDisabled}
                          onChange={handleToggleSpeakerDiarization}
                        >
                          <Box display="inline-flex" gap={16}>
                            <ToggleCheckbox />
                            <ToggleLabel color="secondary" variant="secondary">
                              {TEXT.labelSpeakers}
                            </ToggleLabel>
                          </Box>
                        </Toggle>
                      </CheckboxWrapper>
                    </motion.div>
                  )}
                </AnimatePresence>
              </Box>
            </motion.div>
          )}
        </AnimatePresence>
      </TopContent>
      <TranscriptionConsentModal
        isShowing={isConsentModalOpen}
        numberOfFiles={assetIds?.length ?? 0}
        onRequestClose={handleConsentModalClose}
        onSubmit={handleConsentModalSubmit}
      />
      {showFooter && (
        <>
          <Box
            alignItems="center"
            display="flex"
            flexDirection="column"
            gap={8}
            justifyContent="center"
            paddingBottom={showFeedbackButton ? 32 : 0}
            paddingX={32}
            style={{
              marginTop: showFeedbackButton || showNoAudioMessage ? 0 : '-2px',
              marginBottom: showNoAudioMessage
                ? theme.spaces[24]
                : theme.spaces[16],
            }}
          >
            {activeTranscription?.source === 'UPLOAD' ? (
              <Text color="tertiary" textAlign="center" variant="caption">
                {TEXT.captionsDisclaimer}
              </Text>
            ) : (
              <Text
                color="tertiary"
                style={{ maxWidth: '350px' }}
                textAlign="center"
                variant="caption"
              >
                {TEXT.transcriptsDisclaimer()}
              </Text>
            )}

            {showFeedbackButton && (
              <StyledButton onPress={onFeedback} size="small">
                {TEXT.feedback}
              </StyledButton>
            )}
          </Box>
        </>
      )}
    </StyledContent>
  );
};
const TriggerMenuAndButton = styled.div`
  display: flex;
  flex-direction: row;
  gap: 8px;
  justify-content: center;
`;
const LoaderContainer = styled.div`
  height: ${theme.spaces[24]};
  margin-top: 40px;
  width: 100%;
`;
export const StyledBodyText = styled(Text)`
  font-size: ${theme.fontSizes[70]};
  line-height: 24px;
  max-width: 300px;
  white-space: normal;
`;
export const TopContent = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  justify-content: center;
`;
export const StyledTranscription = styled.div``;
export const StyledContent = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: ${theme.spaces[8]};
  height: 100%;
  justify-content: center;
  overflow: hidden;

  @container panel-container (max-height: 500px) {
    overflow: auto;
    ${styledScrollbarMixin};
    justify-content: flex-start;
    ${StyledTranscription} {
      display: none;
    }
  }
`;
export const StyledButton = styled(Button2)`
  background-color: transparent;
  color: ${theme.colors.text.link};
  font-size: ${theme.fontSizes[50]};

  &:hover {
    background-color: transparent;
    color: ${theme.colors.components.button.primary.interactive.background
      .hover};
  }
`;
const CheckboxWrapper = styled.span<{
  $disabled?: boolean;
}>`
  max-height: 16px;
  opacity: ${({ $disabled }) => ($disabled ? 0.5 : 1)};
  padding: 3px 0;
`;
const StyledAlertLine = styled(AlertInline)`
  text-align: left;
`;
